import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SegmentedField, BooleanField, SelectField } from '../shared/FormField';
import { SPECIES_CATEGORIES, MANDATE_STATUSES } from '../data/enums';
import { parseApiError } from '../FoundationalTables/SpeciesLivestockRegistry/registryHelpers';

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleString('en-NG', { dateStyle: 'medium', timeStyle: 'short' });
}

function InfoRow({ label, value, wide = false }) {
  return (
    <div className={wide ? 'sm:col-span-2 grid gap-1.5' : 'grid gap-1.5'}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</span>
      <div className="rounded-xl bg-gray-100 px-4 py-3 text-sm text-gray-700">
        {value || '—'}
      </div>
    </div>
  );
}

export default function EditSpeciesModal({ isOpen, onClose, onSave, row }) {
  const [form, setForm] = useState({
    species_code: '',
    common_name_nigeria: '',
    scientific_name: '',
    species_category: '',
    sex_disaggregation_required: null,
    mandate_status: '',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!row) return;

    setForm({
      species_code: row.species_code ?? '',
      common_name_nigeria: row.common_name_nigeria ?? '',
      scientific_name: row.scientific_name ?? '',
      species_category: row.species_category ?? '',
      sex_disaggregation_required: row.sex_disaggregation_required ?? null,
      mandate_status: row.mandate_status ?? '',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const submit = async () => {
    if (submitting || !row?.uuid) return;

    if (
      !form.species_code.trim() ||
      !form.common_name_nigeria.trim() ||
      !form.scientific_name.trim() ||
      !form.species_category ||
      form.sex_disaggregation_required === null ||
      !form.mandate_status
    ) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        uuid: row.uuid,
        species_code: form.species_code.trim(),
        common_name_nigeria: form.common_name_nigeria.trim(),
        scientific_name: form.scientific_name.trim(),
        species_category: form.species_category,
        sex_disaggregation_required: form.sex_disaggregation_required,
        mandate_status: form.mandate_status,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseApiError(error, 'Unable to update species.'));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      title="Edit Species"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Species Updated"
      successMessage="The species changes were saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Species Code"
        required
        value={form.species_code}
        onChange={set('species_code')}
        readOnly
        className="cursor-not-allowed bg-gray-50 font-mono"
        note="System identifier - cannot be edited after creation."
      />
      <TextField
        label="Common Name Nigeria"
        required
        value={form.common_name_nigeria}
        onChange={set('common_name_nigeria')}
      />
      <TextField
        label="Scientific Name"
        required
        value={form.scientific_name}
        onChange={set('scientific_name')}
        placeholder="e.g. Bos indicus"
      />
      <SegmentedField
        label="Species Category"
        required
        value={form.species_category}
        onChange={set('species_category')}
        options={SPECIES_CATEGORIES}
      />
      <BooleanField
        label="Sex Disaggregation Required"
        required
        value={form.sex_disaggregation_required}
        onChange={set('sex_disaggregation_required')}
      />
      <SelectField
        label="Mandate Status"
        required
        value={form.mandate_status}
        onChange={set('mandate_status')}
        options={MANDATE_STATUSES}
        placeholder="Select Status"
      />

      <div className="grid gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 sm:grid-cols-2">
        <p className="text-[13px] font-semibold text-gray-900 sm:col-span-2">Backend Metadata</p>
        <InfoRow label="Mandate Decision Ref" value={row?.mandate_decision_ref || 'Assigned by backend'} />
        <InfoRow label="Is Validated" value={row?.is_validated ? 'Yes' : 'No'} />
        <InfoRow label="Created At" value={formatDate(row?.created_at)} />
        <InfoRow label="Updated At" value={formatDate(row?.updated_at)} />
      </div>
    </Modal>
  );
}
